import { useState } from "react";
import { useLocation } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { formatPrice, formatAddress } from "../lib/format";
import { Search, Package, CheckCircle2, Truck, Home as HomeIcon, ShieldCheck } from "lucide-react";

const STEP_ICONS = {
  paid: ShieldCheck,
  processing: Package,
  packed: Package,
  shipped: Truck,
  out_for_delivery: Truck,
  delivered: HomeIcon,
};

export default function TrackOrder() {
  const search = new URLSearchParams(useLocation().search);
  const [orderId, setOrderId] = useState(search.get("order_id") || "");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const lookup = async (e) => {
    e?.preventDefault?.();
    setErr("");
    setData(null);
    if (!orderId.trim() || !phone.trim()) { setErr("Order ID and phone number are required"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/track", { order_id: orderId.trim(), phone: phone.trim() });
      setData(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-28 pb-24 px-6 md:px-12">
      <div className="max-w-3xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">Atelier Care</p>
        <h1 className="font-serif text-4xl md:text-5xl mb-3">Track your order</h1>
        <p className="text-muted text-sm mb-10 max-w-xl">Enter your Order ID and the phone number you used at checkout — the last 4 digits are enough.</p>

        <form onSubmit={lookup} className="bg-[#141414] p-6 md:p-8 space-y-5" data-testid="track-form">
          <div className="grid sm:grid-cols-[1fr_240px] gap-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Order ID</label>
              <input
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="ord_xxxxxxxxxx"
                className="input-line w-full"
                data-testid="track-order-id"
                autoFocus
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-muted block mb-1.5">Phone (full or last 4)</label>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="9876543210"
                className="input-line w-full"
                data-testid="track-phone"
              />
            </div>
          </div>
          <button type="submit" disabled={loading} className="btn-gold" data-testid="track-submit">
            {loading ? "Looking up…" : <><Search size={14} strokeWidth={1.5} className="inline mr-2 -mt-0.5" />Track Order</>}
          </button>
          {err && <p className="text-xs text-red-400 uppercase tracking-[0.18em]" data-testid="track-error">{err}</p>}
        </form>

        {data && <TrackResult data={data} />}
      </div>
    </div>
  );
}

function TrackResult({ data }) {
  const live = data.courier?.live;
  return (
    <div className="mt-12 space-y-10" data-testid="track-result">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted">Order</p>
          <h2 className="font-serif text-3xl text-gold">{data.order_id}</h2>
          <p className="text-xs text-muted mt-1">Placed on {new Date(data.created_at).toLocaleDateString()}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-[0.25em] text-muted">Total</p>
          <p className="font-serif text-3xl">{formatPrice(data.total)}</p>
        </div>
      </header>

      {/* Timeline */}
      <div className="bg-[#141414] p-6 md:p-8">
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-6">Journey</p>
        <ol className="space-y-5">
          {data.timeline.map((step, idx) => {
            const Icon = STEP_ICONS[step.code] || CheckCircle2;
            return (
              <li key={step.code} className="flex gap-4 items-start" data-testid={`step-${step.code}`}>
                <div className={`relative w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${step.reached ? "bg-[#C8A97E] text-[#0C0C0C]" : "bg-[#1a1a1a] text-muted border border-[rgba(244,244,240,0.06)]"}`}>
                  <Icon size={16} strokeWidth={1.5} />
                  {idx < data.timeline.length - 1 && (
                    <span className={`absolute top-10 left-1/2 -translate-x-1/2 w-px h-5 ${step.reached ? "bg-[rgba(200,169,126,0.5)]" : "bg-[rgba(244,244,240,0.08)]"}`} />
                  )}
                </div>
                <div className="flex-1 pb-3">
                  <p className={`text-sm ${step.current ? "text-gold font-medium" : step.reached ? "text-text" : "text-muted/60"}`}>
                    {step.label}
                  </p>
                  {step.at && step.reached && (
                    <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-1">{new Date(step.at).toLocaleString()}</p>
                  )}
                </div>
                {step.current && <span className="text-[10px] uppercase tracking-[0.18em] text-gold border border-gold/30 px-2 py-1 self-center">Current</span>}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Live courier */}
      {data.courier?.awb && (
        <div className="bg-[#141414] p-6 md:p-8 space-y-3" data-testid="track-courier">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Courier</p>
          <div className="flex items-center justify-between flex-wrap gap-3 text-sm">
            <div>
              <p>AWB · <span className="text-gold font-mono">{data.courier.awb}</span></p>
              {live?.ok && live.courier_name && <p className="text-muted text-xs mt-1">{live.courier_name}{live.etd ? ` · ETA ${live.etd}` : ""}</p>}
            </div>
            <a href={data.courier.tracking_url} target="_blank" rel="noreferrer" className="btn-outline !px-4 !py-2 text-[10px]">
              View on Shiprocket →
            </a>
          </div>
          {live?.ok && live.activities?.length > 0 && (
            <ul className="mt-4 space-y-3 text-xs">
              {live.activities.slice(0, 6).map((a, i) => (
                <li key={`${a.date}-${i}`} className="border-l border-[rgba(200,169,126,0.3)] pl-3">
                  <p className="text-text">{a.status}</p>
                  <p className="text-muted">{a.date}{a.location ? ` · ${a.location}` : ""}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Items */}
      <div className="bg-[#141414] p-6 md:p-8">
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-4">Items</p>
        <ul className="space-y-4">
          {data.items.map((i, idx) => (
            <li key={`${i.name}-${idx}`} className="flex gap-4 items-center">
              {i.image && <img src={i.image} alt="" className="w-14 h-20 object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="font-serif text-sm truncate">{i.name}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-1">Size {i.size} · Qty {i.quantity}</p>
              </div>
              <p className="text-sm text-gold">{formatPrice((i.price || 0) * (i.quantity || 1))}</p>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-muted mt-6 leading-relaxed">
          Shipping to {data.shipping_to?.name} · {formatAddress(data.shipping_to || {}, ["city", "state", "zip"])}
        </p>
      </div>
    </div>
  );
}
